// src/pages/Community/Missions/FeedbackModal.jsx
import React, { useState, useMemo } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../../firebase";
import "./css/FeedbackModal.css";

/**
 * Props:
 *  - open: boolean
 *  - onClose: function
 *  - mission: object (optional)
 *  - sourceComponent: string (optional)  // e.g. "MissionDetails", "CharacterDetail"
 *  - source: string (optional)           // optional explicit page path or identifier
 */
export default function FeedbackModal({
  open,
  onClose,
  mission,
  sourceComponent = null,
  source = null,
}) {
  const [type, setType] = useState("improvement"); // improvement | mistake | other
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(
    () => details.trim().length >= 10 && !submitting,
    [details, submitting]
  );

  // resolve source information (prefer explicit props; fallback to location/title)
  const resolvedSourcePath =
    source ||
    (typeof window !== "undefined" && window.location && window.location.pathname
      ? window.location.pathname
      : null);

  const resolvedPageTitle =
    (typeof window !== "undefined" && document && document.title) || null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      await addDoc(collection(db, "mission_feedback"), {
        missionId: mission?.id ?? null,
        missionName: mission?.missionName ?? null,
        category: mission?.category ?? null,
        type, // "improvement" | "mistake" | "other"
        details: details.trim(),
        // source metadata
        sourceComponent: sourceComponent || null, // e.g. "MissionDetails"
        sourcePath: resolvedSourcePath, // e.g. "/missions/123"
        pageTitle: resolvedPageTitle,
        status: "open",
        createdAt: serverTimestamp(),
        user: user
          ? {
              uid: user.uid,
              displayName: user.displayName || null,
              email: user.email || null,
            }
          : null,
      });
      setDone(true);
      setDetails("");
      setType("improvement");
    } catch (err) {
      console.error(err);
      setError("Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fbx-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="fbx-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="fbx-head">
          <h3 className="fbx-title">Suggest improvement / Report mistake</h3>
          <button className="fbx-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {!done ? (
          <form onSubmit={onSubmit} className="fbx-form">
            <div className="fbx-row">
              <label className="fbx-label">Type</label>
              <div className="fbx-radio">
                <label>
                  <input
                    type="radio"
                    name="ftype"
                    value="improvement"
                    checked={type === "improvement"}
                    onChange={() => setType("improvement")}
                  />{" "}
                  Improvement
                </label>
                <label>
                  <input
                    type="radio"
                    name="ftype"
                    value="mistake"
                    checked={type === "mistake"}
                    onChange={() => setType("mistake")}
                  />{" "}
                  Mistake / Incorrect info
                </label>
                <label>
                  <input
                    type="radio"
                    name="ftype"
                    value="other"
                    checked={type === "other"}
                    onChange={() => setType("other")}
                  />{" "}
                  Other
                </label>
              </div>
            </div>

            <div className="fbx-row">
              <label htmlFor="fbx-details" className="fbx-label">
                Details <span className="fbx-req">*</span>
              </label>
              <textarea
                id="fbx-details"
                className="fbx-input"
                rows={6}
                minLength={10}
                placeholder="Describe the improvement or the mistake you noticed. Be as specific as possible."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                required
              />
              <div className="fbx-hint">Minimum 10 characters.</div>
            </div>

            {error && <div className="fbx-error" role="alert">{error}</div>}

            <div className="fbx-actions">
              <button type="button" className="fbx-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="fbx-btn fbx-btn--primary" disabled={!canSubmit}>
                {submitting ? "Sending…" : "Send to admin"}
              </button>
            </div>
          </form>
        ) : (
          <div className="fbx-done">
            <div className="fbx-emoji">🎉</div>
            <h4>Thanks for your help!</h4>
            <p>We’ve sent your message to the admin team for review.</p>
            <button className="fbx-btn fbx-btn--primary" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
